import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import CryptoJS from "crypto-js";
import { useNavigate } from "react-router-dom";
import { API_URL, secret_key, storedData } from "./Constants";
import loadingGif from "../assets/loading.gif";
import "./Attendance.css";

const Attendance = () => {
  const navigate = useNavigate();

  // --- Decrypt Token ---
  const encryptedToken = localStorage.getItem("token");
  let decryptedToken = "";

  if (encryptedToken) {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedToken, secret_key);
      decryptedToken = bytes.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      console.error("Token decryption error:", error);
    }
  }

  let loggedUserData = null;

  if (storedData) {
    const bytes = CryptoJS.AES.decrypt(storedData, secret_key);
    loggedUserData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  }

  const [loading, setLoading] = useState(false);
  const [attendance, setAttendance] = useState([]);

  // --- Fetch Attendance ---
  const fetchAttendance = async () => {
    if (!loggedUserData) return;

    setLoading(true);

    try {
      const res = await axios.get(
        `${API_URL}/attendance/${loggedUserData.company_id}/${loggedUserData.parent_no}`,
        {
          headers: {
            Authorization: `CustomToken ${secret_key}`,
          },
        }
      );

      if (res.data.success) {
        setAttendance(res.data.details || []);
      } else {
        Swal.fire("Info", res.data.message || "No attendance found", "info");
      }
    } catch (err) {
      console.error("Fetch attendance error:", err);

      Swal.fire({
        title: "API Error",
        text: "Failed to load attendance records.",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendance();
  }, []);

  // --- Status Badge ---
const statusBadge = (status) => {

  if (status === 1 || status === "1") {
    return <span className="attendance-badge present">Present</span>;
  }

  if (status === 0 || status === "0") {
    return <span className="attendance-badge absent">Absent</span>;
  }

  return <span className="attendance-badge unknown">N/A</span>;
};

  return (
<div className="dashboard-container container-fluid position-relative">

{/* Loading */}
{loading && (
<div className="loading-overlay d-flex align-items-center justify-content-center">
<img className="loading-img" src={loadingGif} alt="Loading..." />
</div>
)}

{/* Header */}
<header className="dashboard-header py-3 mb-4 border-bottom">
<div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center">

<h1 className="fw-bold mb-2 mb-md-0">
<i className="fas fa-calendar-check me-2 text-primary"></i>
Attendance
</h1>

<nav>
<ol className="breadcrumb mb-0">
<li className="breadcrumb-item"><a href="/">Home</a></li>
<li className="breadcrumb-item active">Attendance</li>
</ol>
</nav>

</div>
</header>


<div className="row">

{/* ================= MAIN CONTENT ================= */}
<div className="col-12 col-lg-8">

<div className="card shadow-md rounded-4 border-0 mb-4">

<div className="card-header bg-gradient-primary text-white rounded-top-4 py-3">
<h5 className="mb-0">
<i className="fas fa-list me-2"></i>
Latest Attendance Records
</h5>
</div>

<div className="card-body p-0">

{/* ===== DESKTOP TABLE ===== */}

<div className="table-responsive d-none d-md-block">

<table className="table table-hover align-middle mb-0">

<thead className="table-light">
<tr>
<th>Student</th>
<th>Date</th>
<th>Morning</th>
<th>Evening</th>
</tr>
</thead>

<tbody>

{attendance.length > 0 ? (

attendance.map((attend,i)=>(
<tr key={i}>

<td className="student-name">
<i className="fas fa-user-graduate me-2 text-primary"></i>
{attend.full_name}
</td>

<td>
<span className="badge badge-due">
{new Date(attend.Document_date).toLocaleDateString()}
</span>
</td>

<td>{statusBadge(attend.morning_status)}</td>

<td>{statusBadge(attend.evening_status)}</td>

</tr>
))

) : (

<tr>
<td colSpan="4" className="text-center text-muted py-4">
<i className="fas fa-calendar-times fa-lg d-block mb-2"></i>
No attendance records found.
</td>
</tr>

)}

</tbody>

</table>

</div>


{/* ===== MOBILE CARDS ===== */}

<div className="d-md-none p-3">

{attendance.length > 0 ? (

attendance.map((attend,i)=>(

<div className="attendance-card mb-3" key={i}>

<div className="attendance-card-header">

<strong>
<i className="fas fa-user-graduate me-2 text-primary"></i>
{attend.full_name}
</strong>

<span className="badge badge-due">
{new Date(attend.Document_date).toLocaleDateString()}
</span>

</div>

<div className="attendance-card-body">

<div className="attendance-row">
<span>Morning</span>
{statusBadge(attend.morning_status)}
</div>

<div className="attendance-row">
<span>Evening</span>
{statusBadge(attend.evening_status)}
</div>

</div>

</div>

))

) : (

<div className="text-center text-muted py-4">
<i className="fas fa-calendar-times fa-lg d-block mb-2"></i>
No attendance records found.
</div>

)}

</div>

</div>
</div>

</div>


{/* ================= SIDEBAR ================= */}

<div className="col-12 col-lg-4">

<div className="card shadow-lg border-0 rounded-4 attendance-notes">

<div className="card-header bg-gradient-secondary text-white rounded-top-4 py-3">
<h5 className="mb-0">
<i className="fas fa-info-circle me-2"></i>
Attendance Notes
</h5>
</div>

<div className="card-body">

<p className="text-muted mb-3">
This section shows the latest attendance records for your child.
</p>

<ul className="list-group list-group-flush small">

<li className="list-group-item">
<i className="fas fa-check-circle text-success me-2"></i>
Green badge indicates the student was present.
</li>

<li className="list-group-item">
<i className="fas fa-times-circle text-danger me-2"></i>
Red badge indicates the student was absent.
</li>

<li className="list-group-item">
<i className="fas fa-clock text-warning me-2"></i>
Yellow badge indicates late attendance.
</li>

</ul>

</div>

</div>

</div>

</div>

</div>
);
};

export default Attendance;
